import { z } from "zod";

import { airlinePreferenceSchema } from "../airlines";
import { MAX_DESTINATIONS, MAX_ORIGIN_COUNT } from "../constants";
import type { FaresSnapshot } from "./ui-types";

/** A link or image from the flight search: https only, so a stored value can't be a script URL. */
const httpsUrl = z
  .string()
  .max(2000)
  .refine((value) => /^https:\/\/[^\s]+$/i.test(value))
  .nullable();
const text = (max: number) => z.string().max(max);
const count = z.number().finite().min(0).max(1_000_000);

const flightDetails = z.object({
  legs: z
    .array(
      z.object({
        airline: text(120).nullable(),
        flightNumber: text(40).nullable(),
        from: text(10).nullable(),
        to: text(10).nullable(),
        departs: text(40).nullable(),
        arrives: text(40).nullable(),
        airplane: text(120).nullable(),
      })
    )
    .max(12),
  via: z.array(text(10)).max(10),
  cabin: text(60).nullable(),
  otherOptions: count,
});

const searchResult = z.object({
  groupName: text(200),
  departureCode: text(10),
  arrivalCode: text(10),
  travelClass: text(40),
  error: text(500).nullable(),
  flight: z
    .object({
      price: count,
      minutes: count,
      layovers: count,
      layoverMinutes: count,
      airline: text(120).nullable(),
      flightNumber: text(40).nullable(),
      airlineLogo: httpsUrl,
      carbonKg: count,
      url: httpsUrl,
      details: flightDetails.nullable(),
    })
    .nullable(),
});

const faresSchema = z.object({
  signature: text(20_000),
  entry: z.object({
    plan: z.object({
      groups: z
        .array(
          z.object({
            key: text(400),
            name: text(200),
            place: text(200),
            cabin: z.enum(["economy", "premium economy", "business", "first"]),
            count: z.number().int().min(1).max(MAX_ORIGIN_COUNT),
          })
        )
        .max(100),
      destinations: z.array(text(200)).max(MAX_DESTINATIONS),
      airlines: airlinePreferenceSchema.optional(),
    }),
    started: z.object({ token: text(200).nullable(), airports: z.record(text(200), text(10).nullable()) }).nullable(),
    poll: z
      .object({
        status: z.enum(["searching", "complete", "error"]),
        progress: z.number().finite().min(0).max(1),
        airportNames: z.record(text(10), text(200)),
        results: z.array(searchResult).max(1000),
      })
      .nullable(),
    error: text(500).nullable(),
  }),
});

/**
 * A saved fares snapshot, if it is one the flight search could have written: known fields of
 * bounded size, numbers where numbers belong, and https-only links and airline logos. Anything
 * else (a hand-made `javascript:` link, a huge array) drops the whole snapshot, and the estimate
 * simply searches fares again. Checked on save and on read.
 * @param raw - The stored or submitted JSON.
 * @returns The snapshot, or null when it doesn't pass.
 * @throws Never.
 */
export const parseFaresSnapshot = (raw: unknown): FaresSnapshot | null => {
  const parsed = faresSchema.safeParse(raw);
  return parsed.success ? (parsed.data as FaresSnapshot) : null;
};

/**
 * A link or image address safe to put in `href` or `src`: http(s) only.
 * @param url - The address.
 * @returns It, or undefined for anything else (`javascript:`, `data:`, relative).
 * @throws Never.
 */
export const safeUrl = (url: string | null | undefined): string | undefined =>
  url && /^https?:\/\//i.test(url) ? url : undefined;
